import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAddresses } from '@entities/schedules';
import { yupResolver } from '@hookform/resolvers/yup';

import xhr from '@shared/config/api';
import type { ScheduleAddress } from '@shared/types/schedules';

import { defaultAddressValue, normalizeScheduleAddress, schema } from './lib';

export const useAddressLoader = () => {
	const { setAddresses, selectAddress } = useAddresses();

	useEffect(() => {
		const conroller = new AbortController();
		const loadAddresses = async () => {
			try {
				const res = await xhr.get<ScheduleAddress[]>(`/api/v1/schedules/address/`, {
					signal: conroller.signal,
				});

				if (!res.data) throw res;

				if (res.data.length === 0) return;

				setAddresses(res.data.sort((a, b) => Number(a.order) - Number(b.order)));
				selectAddress(res.data[0].id);
			} catch (error) {
				console.log(error);
			}
		};

		loadAddresses();

		return () => {
			conroller.abort();
		};
	}, []);
};

export const useAddressForm = () => {
	const { addresses, removeAddress, addAddress, activeAddressId, selectAddress } = useAddresses();
	const [isLoading, setIsLoading] = useState(false);
	const [openForm, setOpenForm] = useState<boolean>(false);

	const methods = useForm({
		resolver: yupResolver(schema),
		reValidateMode: 'onSubmit',
		defaultValues: {
			address: defaultAddressValue,
		},
	});

	const resetFields = () => {
		methods.resetField('address');
	};

	const openFormAddress = () => {
		setOpenForm((previousValue) => !previousValue);
	};

	const onSubmit = async (data: any) => {
		setIsLoading(true);

		try {
			const res = await xhr.post<ScheduleAddress>(
				`/api/v1/schedules/address/`,
				normalizeScheduleAddress(data, addresses)
			);

			if (!res.data) throw res;

			addAddress(res.data);
		} catch (error) {
			console.log(error);
		}

		resetFields();
		setIsLoading(false);
		openFormAddress();
	};

	const deleteAddress = async (id: number) => {
		setIsLoading(true);
		try {
			await xhr.delete(`/api/v1/schedules/address/${id}/`);

			if (id === activeAddressId) selectAddress(null);
		} catch (error) {
			console.log(error);
		}
		removeAddress(id);
		setIsLoading(false);
	};

	return {
		methods,
		onSubmit,
		addresses,
		deleteAddress,
		resetFields,
		openFormAddress,
		openForm,
		isLoading,
	};
};
